import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import {
  Loader2, CheckCircle2, XCircle, AlertTriangle, ShieldCheck,
  Eraser, Building2, Camera, Calendar, Clock, MapPin, Truck,
  PackageCheck, Stamp, FileText, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { toast } from '../../../hooks/use-toast';
import CorrectionRequestModal from '../CorrectionRequestModal';
import composeSignatureWithStamp from '../../../lib/companyStampOverlay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PM Spec V3 13/05/2026 — BaobabOperatorReviewFlow
 *
 * Vue de validation à distance pour l'opérateur Baobab :
 *   - Récap dense des données saisies par les acteurs terrain
 *   - Galerie photos cliquable
 *   - Signatures terrain visibles + état (signé/manquant)
 *   - 3 actions : ✅ Valider / ⚠ Demander correction / ❌ Refuser
 *   - Au clic Valider : canvas signature + cachet entreprise auto-superposé
 *
 * Props :
 *   type, party='baobab_operator', operation, operatorName,
 *   endpointPrefix, authHeaders, linkId, onCompleted
 */
const BaobabOperatorReviewFlow = ({
  type, operation, operatorName,
  endpointPrefix, authHeaders, linkId, noteId,
  onCompleted,
}) => {
  // Bug fix 16/02/2026 — Stabilise axios.create via useState lazy init.
  // Évite la boucle infinie : axios.create recréé chaque render → useCallback fetchNote
  // recréé → useEffect re-trigger fetchNote → setLoading(true) → re-render → boucle.
  const [ax] = useState(() => axios.create({ headers: authHeaders || {} }));
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stampBase64, setStampBase64] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCorrection, setShowCorrection] = useState(false);
  const [showRejection, setShowRejection] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [photoIndex, setPhotoIndex] = useState(null); // photo plein écran
  const [showPhotos, setShowPhotos] = useState(true);
  const [success, setSuccess] = useState(null); // 'validated' | 'rejected' | 'correction'
  const [gps, setGps] = useState(null);
  const sigRef = useRef(null);

  // GPS silencieux
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {}, { timeout: 5000 }
    );
  }, []);

  // Load note + branding stamp
  // PM Bug fix 17/02/2026 — Priorité au noteId scopé (JWT) en multi-rotation
  const fetchNote = useCallback(async () => {
    setLoading(true);
    try {
      const list = await ax.get(endpointPrefix);
      const allNotes = list.data || [];
      let found;
      if (noteId) found = allNotes.find(n => n.id === noteId);
      if (!found && linkId) {
        found = allNotes.find(n =>
          (n.signatureLinks || []).some(l => l.id === linkId)
        );
      }
      if (!found) found = allNotes.find(n => n.type === type);
      setNote(found || null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [endpointPrefix, type, ax, linkId, noteId]);

  const fetchStamp = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/system-config/branding/public`);
      const branding = r.data || {};
      if (branding.company_stamp_base64) {
        setStampBase64(branding.company_stamp_base64);
      }
    } catch { /* non-blocking */ }
  }, []);

  useEffect(() => { fetchNote(); fetchStamp(); }, [fetchNote, fetchStamp]);

  if (loading) {
    return (
      <Card data-testid="operator-review-loading">
        <CardContent className="p-8 text-center text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Chargement du dossier…
        </CardContent>
      </Card>
    );
  }

  if (!note) {
    return (
      <Card data-testid="operator-review-no-note" className="border-amber-200 bg-amber-50">
        <CardContent className="p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-amber-600 mx-auto mb-2" />
          <div className="font-semibold text-amber-800">Aucun bon disponible</div>
          <div className="text-xs text-slate-600">Le bon n'a pas encore été créé par les acteurs terrain.</div>
        </CardContent>
      </Card>
    );
  }

  const sigs = note.signatures || [];
  const photos = note.photos || [];
  const isLoading = type === 'loading';
  const fieldParties = isLoading ? ['shipper', 'driver'] : ['consignee', 'driver'];
  const partiesSigned = fieldParties.map(p => ({
    party: p,
    sig: sigs.find(s => s.party === p),
  }));
  const allFieldSigned = partiesSigned.every(p => !!p.sig);
  const operatorAlreadySigned = sigs.some(s => s.party === 'baobab_operator');
  const rejected = !!note.rejectedAt;

  // ─── Success states ───
  if (success === 'validated') {
    return (
      <Card className="border-emerald-200 bg-emerald-50" data-testid="operator-review-validated">
        <CardContent className="p-8 text-center space-y-3">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-600 flex items-center justify-center">
            <ShieldCheck className="w-9 h-9 text-white" />
          </div>
          <div className="text-xl font-bold text-emerald-800">Bon validé avec votre cachet !</div>
          <div className="text-sm text-slate-600">{
            allFieldSigned
              ? 'Le bon est désormais clôturé (3/3 signatures)'
              : 'Pré-validation enregistrée — en attente des signatures terrain.'
          }</div>
        </CardContent>
      </Card>
    );
  }
  if (success === 'rejected') {
    return (
      <Card className="border-red-200 bg-red-50" data-testid="operator-review-rejected">
        <CardContent className="p-8 text-center space-y-3">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-600 flex items-center justify-center">
            <XCircle className="w-9 h-9 text-white" />
          </div>
          <div className="text-xl font-bold text-red-800">Bon rejeté</div>
          <div className="text-sm text-slate-600">L'admin sera notifié.</div>
        </CardContent>
      </Card>
    );
  }
  if (success === 'correction') {
    return (
      <Card className="border-amber-200 bg-amber-50" data-testid="operator-review-correction">
        <CardContent className="p-8 text-center space-y-3">
          <div className="mx-auto w-16 h-16 rounded-full bg-amber-600 flex items-center justify-center">
            <AlertTriangle className="w-9 h-9 text-white" />
          </div>
          <div className="text-xl font-bold text-amber-800">Demande de correction envoyée</div>
          <div className="text-sm text-slate-600">Les parties ciblées vont recevoir un nouveau lien.</div>
        </CardContent>
      </Card>
    );
  }

  // ─── Validate handler ───
  const handleValidate = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast({ title: 'Signature requise', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const sigData = sigRef.current.getCanvas().toDataURL('image/png');
      const composite = await composeSignatureWithStamp(sigData, stampBase64, 'medium');
      await ax.post(`${endpointPrefix}/${note.id}/operator-validate`, {
        action: 'validate',
        signatureBase64: sigData,
        signatureWithStampBase64: composite,
        gpsCoords: gps,
      });
      setSuccess('validated');
      onCompleted?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur';
      toast({ title: 'Échec validation', description: detail, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason || rejectionReason.length < 5) {
      toast({ title: 'Motif requis (≥ 5 caractères)', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await ax.post(`${endpointPrefix}/${note.id}/operator-validate`, {
        action: 'reject',
        rejectionReason,
      });
      setSuccess('rejected');
      onCompleted?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur';
      toast({ title: 'Échec', description: detail, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCorrection = async (payload) => {
    setSubmitting(true);
    try {
      await ax.post(`${endpointPrefix}/${note.id}/correction-request`, payload);
      setSuccess('correction');
      onCompleted?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur';
      toast({ title: 'Échec', description: detail, variant: 'destructive' });
    } finally {
      setSubmitting(false);
      setShowCorrection(false);
    }
  };

  // ─── Main view : récap dense ───
  return (
    <div className="space-y-4">
      <Card className="border-emerald-200" data-testid="operator-review-main">
        <CardContent className="p-5 space-y-4">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-lg font-bold text-slate-900">Validation à distance</div>
              <div className="text-xs text-slate-500">
                {isLoading ? 'Bon de chargement' : 'Bon de déchargement'} — {note.noteNumber}
              </div>
            </div>
            {note.preValidatedByOperatorAt && (
              <Badge className="bg-blue-600 text-white">Pré-validé</Badge>
            )}
            {rejected && (
              <Badge className="bg-red-600 text-white">Rejeté</Badge>
            )}
          </div>

          {/* Status signatures terrain */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {partiesSigned.map(({ party, sig }) => (
              <div key={party}
                   className={`rounded-lg border-2 p-3 ${sig ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-200'}`}
                   data-testid={`operator-review-party-${party}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase font-bold text-slate-600">
                    {party === 'shipper' ? 'Représentant départ'
                      : party === 'consignee' ? 'Représentant arrivée'
                      : 'Chauffeur'}
                  </div>
                  {sig ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                       : <Clock className="w-5 h-5 text-slate-400" />}
                </div>
                <div className="text-sm font-medium text-slate-900 mt-1">
                  {sig ? sig.name : 'En attente'}
                </div>
                {sig?.signedAt && (
                  <div className="text-xs text-slate-500">
                    {new Date(sig.signedAt).toLocaleString('fr-FR')}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Données saisies */}
          <div className="rounded-lg border border-slate-200 p-3 space-y-1.5 text-sm">
            <div className="text-xs uppercase font-bold text-slate-500 mb-1">Données saisies</div>
            <div className="grid grid-cols-2 gap-1.5">
              <div><span className="text-slate-500">Date :</span> <span className="font-medium">{note.actualDate || '—'}</span></div>
              <div><span className="text-slate-500">Arrivée :</span> <span className="font-medium">{note.arrivalTime || '—'}</span></div>
              <div><span className="text-slate-500">Début :</span> <span className="font-medium">{note.startTime || '—'}</span></div>
              <div><span className="text-slate-500">Fin :</span> <span className="font-medium">{note.endTime || '—'}</span></div>
              <div><span className="text-slate-500">Poids :</span> <span className="font-medium">{note.actualWeight ? `${note.actualWeight} T` : '—'}</span></div>
              <div><span className="text-slate-500">État :</span> <span className="font-medium">{note.actualState || '—'}</span></div>
            </div>
            {note.observations && (
              <div className="pt-2 border-t border-slate-100">
                <div className="text-slate-500 text-xs mb-0.5">Observations</div>
                <div className="text-sm leading-snug">{note.observations}</div>
              </div>
            )}
            {note.reserveDescription && (
              <div className="pt-2 border-t border-slate-100">
                <div className="text-amber-600 text-xs font-bold mb-0.5">⚠ Réserves</div>
                <div className="text-sm leading-snug text-amber-900">{note.reserveDescription}</div>
              </div>
            )}
          </div>

          {/* Photos */}
          <div className="rounded-lg border border-slate-200 p-3">
            <button
              type="button"
              data-testid="operator-review-photos-toggle"
              className="w-full flex items-center justify-between text-sm font-bold text-slate-700"
              onClick={() => setShowPhotos(!showPhotos)}
            >
              <span className="flex items-center gap-1.5">
                <Camera className="w-4 h-4" /> {photos.length} photos
              </span>
              {showPhotos ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showPhotos && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
                {photos.map((p, i) => (
                  <button
                    key={p.id || i}
                    type="button"
                    data-testid={`operator-review-photo-${i}`}
                    onClick={() => setPhotoIndex(i)}
                    className="aspect-square rounded-md overflow-hidden border border-slate-200 hover:border-emerald-500"
                  >
                    <img src={p.base64} alt={p.label} className="w-full h-full object-cover" />
                  </button>
                ))}
                {photos.length === 0 && <div className="col-span-full text-center text-xs text-slate-500 py-3">Aucune photo</div>}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Decision area */}
      {!operatorAlreadySigned && !rejected && (
        <Card className="border-emerald-300" data-testid="operator-review-decision">
          <CardContent className="p-5 space-y-4">
            {!showSignature && !showRejection && (
              <>
                <div className="text-base font-bold text-slate-900">Votre décision</div>
                <div className="grid gap-2">
                  <Button
                    size="lg"
                    data-testid="operator-action-validate"
                    onClick={() => setShowSignature(true)}
                    className="bg-emerald-600 hover:bg-emerald-700 gap-2 h-14 text-base font-bold"
                  >
                    <CheckCircle2 className="w-5 h-5" /> Je valide et signe
                  </Button>
                  <Button
                    size="lg" variant="outline"
                    data-testid="operator-action-correction"
                    onClick={() => setShowCorrection(true)}
                    className="border-amber-400 text-amber-700 hover:bg-amber-50 gap-2 h-12"
                  >
                    <AlertTriangle className="w-4 h-4" /> Demander correction
                  </Button>
                  <Button
                    size="lg" variant="outline"
                    data-testid="operator-action-reject"
                    onClick={() => setShowRejection(true)}
                    className="border-red-400 text-red-700 hover:bg-red-50 gap-2 h-12"
                  >
                    <XCircle className="w-4 h-4" /> Refuser et bloquer
                  </Button>
                </div>
                {!allFieldSigned && (
                  <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
                    ⚠ Les signatures terrain ne sont pas encore complètes — votre signature sera enregistrée comme <strong>pré-validation</strong>.
                  </div>
                )}
              </>
            )}

            {/* Signature canvas */}
            {showSignature && (
              <div className="space-y-3" data-testid="operator-review-sig-area">
                <div className="text-base font-bold text-slate-900">Apposez votre signature</div>
                <div className="text-xs text-slate-500 flex items-center gap-1.5">
                  <Stamp className="w-3 h-3" />
                  {stampBase64
                    ? 'Le cachet entreprise sera automatiquement superposé'
                    : 'Aucun cachet configuré (admin)'}
                </div>
                <div className="border-2 border-dashed border-emerald-300 rounded-lg bg-white">
                  <SignatureCanvas
                    ref={sigRef}
                    penColor="#0f172a"
                    canvasProps={{ className: 'w-full h-44 rounded-lg', 'data-testid': 'operator-signature-canvas' }}
                  />
                </div>
                <div className="flex flex-wrap gap-2 justify-between">
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm"
                            data-testid="operator-clear-sig"
                            onClick={() => sigRef.current?.clear()}>
                      <Eraser className="w-3.5 h-3.5 mr-1" /> Effacer
                    </Button>
                    <Button variant="ghost" size="sm"
                            data-testid="operator-cancel-sig"
                            onClick={() => setShowSignature(false)}>
                      Retour
                    </Button>
                  </div>
                  <Button size="lg"
                          data-testid="operator-confirm-validate"
                          disabled={submitting}
                          onClick={handleValidate}
                          className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                    Confirmer ma signature
                  </Button>
                </div>
              </div>
            )}

            {/* Rejection form */}
            {showRejection && (
              <div className="space-y-3" data-testid="operator-review-reject-area">
                <div className="text-base font-bold text-red-800">Motif du refus</div>
                <textarea
                  data-testid="operator-rejection-reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  className="w-full p-2 border border-red-200 rounded-md text-sm"
                  placeholder="Expliquez pourquoi le bon ne peut pas être validé…"
                />
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setShowRejection(false)}
                          data-testid="operator-cancel-reject">
                    Retour
                  </Button>
                  <Button variant="destructive"
                          data-testid="operator-confirm-reject"
                          disabled={submitting || rejectionReason.length < 5}
                          onClick={handleReject}>
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                    Confirmer le refus
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {operatorAlreadySigned && (
        <Card className="border-emerald-200 bg-emerald-50" data-testid="operator-already-signed">
          <CardContent className="p-4 text-center text-sm text-emerald-800">
            ✅ Vous avez déjà validé ce bon.
          </CardContent>
        </Card>
      )}

      {/* Photo modal */}
      {photoIndex !== null && photos[photoIndex] && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
             data-testid="operator-photo-modal"
             onClick={() => setPhotoIndex(null)}>
          <img src={photos[photoIndex].base64} alt={photos[photoIndex].label}
               className="max-w-full max-h-[90vh] rounded-lg" />
        </div>
      )}

      {/* Correction request modal */}
      <CorrectionRequestModal
        open={showCorrection}
        onOpenChange={setShowCorrection}
        noteType={type}
        submitting={submitting}
        onSubmit={handleCorrection}
      />
    </div>
  );
};

export default BaobabOperatorReviewFlow;
